import { packageLabel } from '@/lib/realEstatePackages';
import React, { useState, useEffect } from 'react';
import { ShareButton } from '@/components/ShareButton';
import { Link } from 'react-router-dom';
import { Badge } from "@/components/ui/badge";
import { Loader2, Camera, MapPin, ArrowRight, Home, Bed, Bath, Ruler, ImageIcon } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function Portfolio() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPortfolio = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE}/api/portfolio/public?limit=50`);
        if (!response.ok) {
          throw new Error('Failed to load portfolio');
        }
        const data = await response.json();
        setItems(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPortfolio();
  }, []);

  // Format package name for display
  const formatPackage = (pkg) => (pkg ? packageLabel(pkg) : null);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-10 w-10 text-[#C6A14A] animate-spin mx-auto mb-4" />
          <p className="text-[#E8E4DE]/60 text-sm">Loading portfolio...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, #C6A14A 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }} />
        </div>
        
        <div className="relative container mx-auto px-6 text-center max-w-4xl">
          <Badge className="mb-6 bg-[#C6A14A]/10 text-[#C6A14A] border-[#C6A14A]/30 px-4 py-1.5 text-xs font-bold tracking-widest uppercase">
            Portfolio
          </Badge>
          <h1 className="font-light text-4xl md:text-5xl lg:text-6xl text-white mb-6 tracking-wide">
            Recent Listings
          </h1>
          <p className="text-lg md:text-xl text-[#E8E4DE]/70 max-w-2xl mx-auto leading-relaxed">
            Professional real estate photography showcasing properties across Arizona.
          </p>
        </div>
      </section>

      {/* Portfolio Grid */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {error ? (
            <div className="text-center py-20">
              <p className="text-[#E8E4DE]/60">{error}</p>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-20">
              <div className="p-6 bg-white/5 rounded-full w-24 h-24 mx-auto mb-6 flex items-center justify-center">
                <Camera className="h-10 w-10 text-[#E8E4DE]/30" />
              </div>
              <h3 className="text-xl text-white mb-2">No Properties Yet</h3>
              <p className="text-[#E8E4DE]/60 max-w-sm mx-auto">
                Check back soon to see our latest real estate photography work.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {items.map((item, index) => (
                <Link
                  key={item.id}
                  to={`/showcase/${item.gallery_slug}`}
                  className="group block"
                  data-testid={`portfolio-item-${item.id}`}
                >
                  <div className="relative rounded-xl overflow-hidden bg-[#12121a] border border-white/5 hover:border-[#C6A14A]/30 transition-all duration-300">
                    {/* Cover Photo - Large */}
                    <div className="aspect-[4/3] relative overflow-hidden">
                      {item.cover_url ? (
                        <img
                          src={item.cover_url}
                          alt={item.address}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          loading={index < 6 ? 'eager' : 'lazy'}
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-[#1a1a24] to-[#12121a] flex items-center justify-center">
                          <div className="text-center">
                            <Home className="h-12 w-12 text-[#C6A14A]/30 mx-auto mb-2" />
                            <p className="text-[#E8E4DE]/30 text-sm">Photos Coming Soon</p>
                          </div>
                        </div>
                      )}
                      
                      {/* Always-visible gradient overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

                      <ShareButton slug={item.gallery_slug} title={item.address} variant="icon"
                        className="absolute top-16 right-4 z-10" testId={`portfolio-card-share-${item.id}`} />
                      
                      {/* Featured Badge */}
                      {item.featured && (
                        <div className="absolute top-4 left-4">
                          <Badge className="bg-[#C6A14A] text-[#0a0a0f] text-xs font-semibold px-3 py-1">
                            Featured
                          </Badge>
                        </div>
                      )}
                      
                      {/* Photo Count Badge */}
                      {item.photo_count > 0 && (
                        <div className="absolute top-4 right-4">
                          <div className="bg-black/60 backdrop-blur-sm text-white px-3 py-1.5 rounded-lg text-sm flex items-center gap-1.5">
                            <ImageIcon className="h-3.5 w-3.5" />
                            {item.photo_count}
                          </div>
                        </div>
                      )}
                      
                      {/* Always-visible address/city overlay */}
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <h3 className="text-white text-lg font-medium mb-1 line-clamp-1 group-hover:text-[#C6A14A] transition-colors">
                          {item.address}
                        </h3>
                        <div className="flex items-center gap-2 text-white/70">
                          <MapPin className="h-3.5 w-3.5 text-[#C6A14A]" />
                          <span className="text-sm">{item.city}{item.state && `, ${item.state}`}</span>
                        </div>
                      </div>
                      
                      {/* Hover cue - View Gallery */}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="bg-[#C6A14A] text-[#0a0a0f] px-5 py-2.5 rounded-lg text-sm font-semibold shadow-lg flex items-center gap-2">
                          View Gallery
                          <ArrowRight className="h-4 w-4" />
                        </div>
                      </div>
                    </div>
                    
                    {/* Property Details below image (if available) */}
                    {(item.beds || item.baths || item.sqft || item.package) && (
                      <div className="px-4 py-3 border-t border-white/5">
                        <div className="flex flex-wrap items-center gap-3">
                          {item.beds && (
                            <div className="flex items-center gap-1 text-[#E8E4DE]/50 text-sm">
                              <Bed className="h-3.5 w-3.5" />
                              <span>{item.beds}</span>
                            </div>
                          )}
                          {item.baths && (
                            <div className="flex items-center gap-1 text-[#E8E4DE]/50 text-sm">
                              <Bath className="h-3.5 w-3.5" />
                              <span>{item.baths}</span>
                            </div>
                          )}
                          {item.sqft && (
                            <div className="flex items-center gap-1 text-[#E8E4DE]/50 text-sm">
                              <Ruler className="h-3.5 w-3.5" />
                              <span>{item.sqft.toLocaleString()} sqft</span>
                            </div>
                          )}
                          {item.package && (
                            <Badge variant="outline" className="border-[#C6A14A]/20 text-[#C6A14A]/70 text-xs ml-auto">
                              {formatPackage(item.package)}
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="pb-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="bg-gradient-to-br from-[#12121a] to-[#1a1a24] border border-[#C6A14A]/20 rounded-2xl p-10 md:p-14">
            <h2 className="text-2xl md:text-3xl font-light text-white mb-4">
              Ready to Showcase Your Listing?
            </h2>
            <p className="text-[#E8E4DE]/70 mb-8 max-w-lg mx-auto">
              Professional real estate photography that helps your properties stand out and sell faster.
            </p>
            <Link
              to="/book"
              className="inline-flex items-center gap-2 bg-[#C6A14A] text-[#0a0a0f] px-8 py-3 rounded-lg font-medium hover:bg-[#D4AF5A] transition-colors"
            >
              Book a Shoot
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
